// Internal page operations only; the backend adapter must supply canonical order and revision.
export function mergeMailWindow<T extends { logicalMessageId: string }>(
  current: readonly T[], incoming: readonly T[], direction: 'previous' | 'next',
  currentRevision: string, incomingRevision: string,
): T[] {
  if (currentRevision !== incomingRevision) throw new Error('Revisione conversazione cambiata.')
  const updates = new Map(incoming.map(item => [item.logicalMessageId, item]))
  const existing = new Set(current.map(item => item.logicalMessageId))
  const retained = current.map(item => updates.get(item.logicalMessageId) || item)
  const added: T[] = []
  for (const item of incoming) {
    if (!existing.has(item.logicalMessageId)) {
      added.push(updates.get(item.logicalMessageId)!)
      existing.add(item.logicalMessageId)
    }
  }
  return direction === 'previous' ? [...added, ...retained] : [...retained, ...added]
}

export interface MailReadingPosition { logicalMessageId: string; offset: number }

// The caller measures immediately before a DOM mutation, not when the network request starts.
export function captureMailReadingPosition(container: HTMLElement): MailReadingPosition | null {
  const viewport = container.getBoundingClientRect()
  for (const element of Array.from(container.querySelectorAll<HTMLElement>('[data-mail-logical-id]'))) {
    const rect = element.getBoundingClientRect()
    if (rect.bottom > viewport.top && rect.top < viewport.bottom) {
      return { logicalMessageId: element.dataset.mailLogicalId!, offset: rect.top - viewport.top }
    }
  }
  return null
}

export function restoreMailReadingPosition(container: HTMLElement, position: MailReadingPosition): boolean {
  // Compare dataset values instead of interpolating untrusted IDs into a CSS selector.
  const element = Array.from(container.querySelectorAll<HTMLElement>('[data-mail-logical-id]'))
    .find(item => item.dataset.mailLogicalId === position.logicalMessageId)
  if (!element) return false
  const offset = element.getBoundingClientRect().top - container.getBoundingClientRect().top
  container.scrollTop += offset - position.offset
  return true
}
